/**
 * Phase 7 Commit J. Shared header chrome — top of every step.
 *
 * Renders: salon name (logo), inline mini-rating, hero block (only on
 * first step / when explicit), trust pills, and the "X% there" reframed
 * progress bar. The progress bar is biased visually (steps 1..N feels
 * faster than (i+1)/N because of a +1.4 nudge) — small perceived-effort
 * win without lying about which step the customer is on.
 */

import type { ComponentChildren } from 'preact';
import { StarIcon, LockIcon } from '../icons';

interface TrustData {
  ratingTenths: number | null;
  reviewCount: number | null;
  recentBookings: number | null;
}

interface Props {
  /** 0-indexed step position (0 = Service, etc.) */
  stepIndex: number;
  /** Total number of steps in the active flow (varies per widget config). */
  totalSteps: number;
  /** Salon display name — appears in the top-left logo slot. */
  salonName: string;
  /** Optional logo URL — replaces the text logo when set. */
  logoUrl?: string | null;
  /** Hero copy — widget headerCopy or campaign headline. */
  hero?: string | null;
  /** Sub-line under the hero — typically the salon name when hero is overridden. */
  sub?: string | null;
  /** Hide hero block (Service step keeps it; later steps drop it for density). */
  showHero?: boolean;
  /** Trust pill data. */
  trust?: TrustData;
  /** Optional deposit-hold copy for the second trust pill ("$25 deposit holds your slot"). */
  depositHoldCopy?: string | null;
  /** Optional extra content inside the chrome (e.g. category-filter banner). */
  children?: ComponentChildren;
}

function progressPct(stepIndex: number, total: number): number {
  // Booking confirmed → bar reads 100. App.tsx passes stepIndex ===
  // flow.length when state.step === 'DONE'; without this guard the
  // [8, 96] clamp below would land the success screen at ~96%.
  if (stepIndex >= total) return 100;
  // Bias the visible progress so the bar feels meaningfully ahead of the
  // step counter — small perceived-effort win.
  return Math.min(96, Math.max(8, Math.round(((stepIndex + 1.4) / total) * 100)));
}

export function Chrome({
  stepIndex,
  totalSteps,
  salonName,
  logoUrl,
  hero,
  sub,
  showHero = true,
  trust,
  depositHoldCopy,
  children,
}: Props) {
  const pct = progressPct(stepIndex, totalSteps);
  const ratingDisplay =
    typeof trust?.ratingTenths === 'number'
      ? (trust.ratingTenths / 10).toFixed(1)
      : null;

  return (
    <div class="sbw-chrome">
      <div class="sbw-toprow">
        {logoUrl ? (
          <img src={logoUrl} alt={salonName} class="sbw-logo" loading="eager" />
        ) : (
          <div class="sbw-logo">{salonName}</div>
        )}
        {ratingDisplay && (
          <div class="sbw-trust-mini">
            <span class="sbw-star">
              <StarIcon />
            </span>
            <span>{ratingDisplay}</span>
            {typeof trust?.reviewCount === 'number' && trust.reviewCount > 0 && (
              <span style={{ opacity: 0.5 }}>
                · {trust.reviewCount.toLocaleString()}
              </span>
            )}
          </div>
        )}
      </div>

      {showHero && (hero || sub) && (
        <div class="sbw-hero-block">
          {hero && <h1 class="sbw-hero">{hero}</h1>}
          {sub && <div class="sbw-sub">{sub}</div>}
        </div>
      )}

      {/* Trust pills always visible at the top — they're the social-proof
          anchor regardless of whether the hero block is shown. */}
      {trust && (
        ((typeof trust.recentBookings === 'number' && trust.recentBookings > 0) ||
          depositHoldCopy) && (
          <div class="sbw-trust-row">
            {typeof trust.recentBookings === 'number' &&
              trust.recentBookings > 0 && (
                <span class="sbw-trust-pill">
                  <span class="sbw-dot" />
                  {trust.recentBookings.toLocaleString()} booked this week
                </span>
              )}
            {depositHoldCopy && (
              <span class="sbw-trust-pill">
                <LockIcon /> {depositHoldCopy}
              </span>
            )}
          </div>
        )
      )}

      {children}

      <div class="sbw-progress">
        <div class="sbw-progress-bar">
          <div class="sbw-progress-fill" style={{ width: `${pct}%` }} />
        </div>
        <span class="sbw-progress-label">{pct}% there</span>
      </div>
    </div>
  );
}
